import { StarIcon } from '@heroicons/react/20/solid';
import { useEffect, useState } from 'react';
import client from '../../api/client';
import GOOGLE_ICON from '../../assets/icons/google.png';

export default function Testimonials() {
	const [reviewIdx, setReviewIdx] = useState(0);
	const [reviews, setReviews] = useState([]);

	// Fetch and normalize review data
	const fetchReviews = async () => {
		try {
			const { data } = await client.get('/testimonials');
			// Normalize each review object:
			// - Use "Name" if available instead of "name"
			// - Ensure "review" is an array
			const normalizedData = data.map((review) => ({
				...review,
				name: review.Name || review.name,
				review: Array.isArray(review.review) ? review.review : [review.review],
			}));
			setReviews(normalizedData);
		} catch (error) {
			console.error(error);
		}
	};

	// Rotate the review every 10 seconds
	useEffect(() => {
		if (reviews.length === 0) return;
		const rotateReview = () => {
			setReviewIdx((prevIdx) => (prevIdx + 1) % reviews.length);
		};
		const intervalId = setInterval(rotateReview, 10000);
		return () => clearInterval(intervalId);
	}, [reviews]);

	useEffect(() => {
		fetchReviews();
	}, []);

	// Define current review for easier access
	const currentReview = reviews.length > 0 ? reviews[reviewIdx] : null;

	return (
		<section className='bg-gray-50 px-6 py-24 sm:py-32 lg:px-8'>
			<div className='min-w-0 flex-1 max-w-5xl mx-auto'>
				<h2 className='text-center text-5xl font-bold text-gray-900 sm:truncate sm:text-3xl mb-4'>
					Customer Testimonials
				</h2>
			</div>
			{currentReview ? (
				<figure className='mx-auto max-w-2xl bg-white p-8 shadow-lg rounded'>
					<div className='flex justify-between items-center'>
						<p className='sr-only'>5 out of 5 stars</p>
						<div className='flex gap-x-1 text-yellow-500'>
							{Array.from({ length: currentReview.rating || 5 }).map((_, i) => (
								<StarIcon
									key={i}
									className='h-5 w-5 flex-none'
									aria-hidden='true'
								/>
							))}
						</div>
					</div>
					<blockquote className='mt-1 text-xl font-semibold leading-8 tracking-tight text-gray-900 sm:text-2xl sm:leading-9'>
						{currentReview.review.map((r, i) => (
							<p
								key={i}
								className='mt-3'
							>
								{r}
							</p>
						))}
					</blockquote>
					<figcaption className='flex gap-x-6 mt-4'>
						<div className='text-sm leading-6 flex items-center space-x-2'>
							{currentReview.userImg ? (
								<img
									src={currentReview.userImg}
									alt={currentReview.name}
									className='h-10 w-10 rounded-full'
								/>
							) : (
								<div className='h-10 w-10 flex items-center justify-center rounded-full bg-gray-200 text-gray-700 font-bold'>
									{currentReview.name ? currentReview.name[0] : '?'}
								</div>
							)}
							<span className='font-semibold text-gray-900'>
								{currentReview.name}
							</span>
						</div>
					</figcaption>
					<div className='flex space-x-3 justify-between items-center mt-10'>
						<a
							target='_blank'
							rel='noopener noreferrer'
							href={currentReview.link}
							className='text-primary-600 hover:text-primary-50 flex gap-x-2 items-center hover:bg-gray-50 p-2 rounded'
						>
							<img
								src={GOOGLE_ICON}
								alt='Google reviews'
								className='h-10 w-10'
							/>
							<div className='text-gray-600'>Read the review</div>
						</a>
						<a
							href='https://g.page/r/CbLH-DBVii-uEBM/review'
							target='_blank'
							rel='noopener noreferrer'
						>
							<button className='px-6 py-2 bg-transparent border-4 text-primary-600 font-semibold text-lg rounded-sm hover:bg-primary-600 hover:text-white hover:border-primary-600 transition ease-in duration-300'>
								Write a Review
							</button>
						</a>
					</div>
				</figure>
			) : (
				<div className='mx-auto max-w-2xl text-center text-gray-600'>
					Loading reviews...
				</div>
			)}
		</section>
	);
}
